import "./combo-chart.scss";

import {cx} from "emotion";
import * as React from "react";

import {cs} from "../../../react/chain-services";
import {UseState} from "../../../react/use-state";
import {isDataValid, loadTileData} from "../common/load-tile-data";
import {keepOnly} from "../../../utils/objects";
import {isComparing} from "../factory/check-compare";
import {getFormatters} from "../factory/formatters/formatters";
import {getComboTooltipOptions} from "./get-combo-tooltip-options";
import {processComboChartData} from "./process-combo-chart-data";
import {getMeasurementAxesOptions} from "../factory/measurement-axes/measurement-axes";
import {getDimensionAxisOptions} from "../factory/dimension-axis/dimension-axis";
import {getComboChartOptions} from "./chart-options/get-combo-chart-options";
import {RenderChart} from "../render-chart/render-chart";
import {SwapAxis} from "../common/swap-axis/swap-axis";
import {MenuOptions} from "../common/menu-options/menu-options";
import {isDimRealDateTime} from "../factory/is-dim-real-date-time";
import {rawDataProcessor} from "../data-processor/raw-data-processor";
import {rawDataManipulator} from "../raw-data-manipulator/raw-data-manipulator";
import {NoDataScreen} from "../common/no-data-screen/no-data-screen";
import {ChartInnerLayout} from "../chart-layout/chart-inner-layout";
import {flatten1} from "../../../utils/collections";
import {ActionMenu} from "../common/action-menu/action-menu";
import {loadTileFields} from "../get-field-color";
import {generateReferenceSeries} from "../reference-lines/generate-reference-series";
import {isDevSonPham} from "../../../../tools/dev/is-dev-sonpham";
import {consumeContext} from "../../../react/context";
import {IgnoreUpdate} from "../../../react/ignore-update";
import {BlankDateWarning} from "../common/blank-date-warning";
import {RemoveTileControlFilter} from "../common/control-filter/remove-tile-control-filter";
import {CurrencyConversionWarning, getCurrencyConversion} from "../common/currency-conversion-warning/currency-conversion-warning";
import {fragments} from "../../../react/fragments";
import {Watch} from "../../../react/watch";

export const ComboChart = ({tile, tileFilters, theme, loadData, downloadData, size, chartRef, defaultData, overrideTile, hasSwapAxis, disabledTileActions = false, noScroll = false}) =>
    cs(
        consumeContext("tileActionControlFilter"),
        [
            "chartData",
            ({tileActionControlFilter}, next) =>
                defaultData
                    ? next(defaultData)
                    : loadTileData({
                          next,
                          tileFilters,
                          loadData,
                          size,
                          tile,
                          theme,
                          tileKey: JSON.stringify([keepOnly(tile, ["xAxisField", "yAxisBarFields", "yAxisLineFields", "groupField", "filters", "limit", "sort", "categorySort"]), tile.style.xAxis.dateGaps]),
                          disabled: tileActionControlFilter?.value?.tile?.id == tile.id,
                      }),
        ],
        consumeContext("selectedTimezone"),
        ["rawData", ({chartData}, next) => next(rawDataManipulator({tile, rawData: chartData}))],
        ({rawData}, next) => (!isDataValid(rawData) ? NoDataScreen({size, theme}) : next()),

        ["isCompare", ({rawData}, next) => next(isComparing(rawData, tile))],
        [
            "isDimensionRealDate",
            ({rawData}, next) =>
                next(
                    isDimRealDateTime({
                        tile,
                        dimensionAttr: "xAxisField",
                        series: rawData.series,
                    })
                ),
        ],

        [
            "rawData2",
            ({isCompare, rawData, isDimensionRealDate}, next) =>
                next(
                    rawDataProcessor({
                        tile,
                        rawData,
                        isDimensionRealDate,
                        isCompare,
                    })
                ),
        ],

        [
            "formatters",
            ({rawData2, selectedTimezone}, next) =>
                next(
                    getFormatters({
                        tile,
                        dimensionAttr: "xAxisField",
                        measurementAttrs: ["yAxisBarFields", "yAxisLineFields"],
                        rawData: rawData2,
                        timezone: selectedTimezone?.value,
                    })
                ),
        ],

        [
            "tooltipOptions",
            ({rawData2, formatters, isCompare, barKey}, next) =>
                next(
                    getComboTooltipOptions({
                        tile,
                        theme,
                        formatters,
                        isCompare,
                        rawData: rawData2,
                        barStacks: Object.keys(tile.style.yAxisBar),
                        lineStacks: Object.keys(tile.style.yAxisLine),
                    })
                ),
        ],

        [
            "tileFields",
            ({}, next) =>
                loadTileFields({
                    next,
                    configs: {
                        tile,
                        dimensionAttr: "xAxisField",
                        measureGroupAttrs: ["yAxisBarFields", "yAxisLineFields"],
                        isBarFields: ["yAxisBarFields"],
                        groupFieldAttr: "groupField",
                        checkUnique: false,
                    },
                }),
        ],

        [
            "data",
            ({tooltipOptions, rawData2, formatters, isCompare, tileFields}, next) =>
                next(
                    processComboChartData({
                        tile,
                        theme,
                        tooltipOptions,
                        formatters,
                        isCompare,
                        tileFields,
                        rawData: rawData2,
                    })
                ),
        ],

        [
            "lineKey",
            ({data}, next) =>
                UseState({
                    next: ({value, onChange}) =>
                        next({
                            value: value == null || data.lineData[value] == null ? Object.keys(data.lineData)[0] : value,
                            onChange,
                        }),
                }),
        ],

        [
            "barKey",
            ({data}, next) =>
                UseState({
                    next: ({value, onChange}) =>
                        next({
                            value: value == null || data.barData[value] == null ? Object.keys(data.barData)[0] : value,
                            onChange,
                        }),
                }),
        ],

        [
            "referenceSeries",
            ({lineKey, barKey, data, formatters}, next) => {
                const getSeries = (dataGroup, multipleAxisOption, key) => {
                    if (!Object.keys(dataGroup).length) {
                        return [];
                    }
                    return multipleAxisOption === "IndependentAxes" ? flatten1(Object.values(dataGroup).map(({series}) => series)) : dataGroup[key].series;
                };

                return next(
                    generateReferenceSeries({
                        tile,
                        theme,
                        formatters,
                        series: [...getSeries(data.barData, tile.style.multipleAxisBarOption, barKey.value), ...getSeries(data.lineData, tile.style.multipleAxisLineOption, lineKey.value)],
                    })
                );
            },
        ],

        [
            "yAxisBarOptions",
            ({formatters, data, rawData2, referenceSeries, isCompare, barKey}, next) =>
                next(
                    getMeasurementAxesOptions({
                        tile,
                        theme,
                        data: data.barData,
                        rawData:
                            tile.style.multipleAxisBarOption === "IndependentAxes"
                                ? rawData2
                                : {
                                      series: rawData2.series.filter((s) => s.stack === barKey.value),
                                  },
                        referenceSeries,
                        isCompare,
                        styleMeasurementAttr: "yAxisBar",
                        measurementFormatters: formatters.measurementFormatters,
                    })
                ),
        ],

        [
            "yAxisLineOptions",
            ({formatters, data, referenceSeries}, next) =>
                next(
                    getMeasurementAxesOptions({
                        tile,
                        theme,
                        data: data.lineData,
                        referenceSeries,
                        styleMeasurementAttr: "yAxisLine",
                        measurementFormatters: formatters.measurementFormatters,
                    })
                ),
        ],

        [
            "xAxisOptions",
            ({formatters, rawData2, data, barKey, lineKey, isCompare}, next) => {
                return next(
                    getDimensionAxisOptions({
                        tile,
                        theme,
                        rawSeries: rawData2.series.filter((s) => [barKey.value, lineKey.value].includes(s.stack)),
                        series: [...(data.barData[barKey.value]?.series || []), ...(data.lineData[lineKey.value]?.series || [])],
                        isCompare,
                        dimensionAttr: "xAxisField",
                        styleDimensionAttr: "xAxis",
                        dimensionFormatter: formatters.dimensionFormatter,
                    })
                );
            },
        ],

        [
            "controls",
            ({data, barKey, lineKey, rawData, tileFields}, next) => {
                const {multipleAxisBarOption, multipleAxisLineOption} = tile.style;
                const lineKeys = Object.keys(data.lineData);
                const barKeys = Object.keys(data.barData);
                const hasMenuOptions = tile.style.showDownloadData || tile.style.showDownloadImage;
                const hasAlternativeBarAxes = multipleAxisBarOption !== "IndependentAxes" && barKeys.length > 1;
                const hasAlternativeLineAxes = multipleAxisLineOption !== "IndependentAxes" && lineKeys.length > 1;
                const showSwapAxis = hasAlternativeBarAxes || hasAlternativeLineAxes;

                const hasCurrencyConversionFailures = rawData.series.filter((s) => s.currencyConversionFailures).length > 0;

                // if (!hasAlternativeBarAxes && !hasAlternativeLineAxes && !hasMenuOptions) {
                //     return next(null);
                // }

                return next(
                    fragments(
                        Watch({
                            value: !!showSwapAxis,
                            onChanged: (changed) => hasSwapAxis?.onChange?.(!!changed),
                            initRun: true,
                        }),
                        <div className="controls">
                            {CurrencyConversionWarning({
                                theme,
                                currencyConversion: getCurrencyConversion({
                                    $type: rawData.$type,
                                    data: rawData,
                                    tileFields,
                                }),
                            })}

                            {BlankDateWarning({
                                isDimensionRealDate: isDimRealDateTime({
                                    tile,
                                    dimensionAttr: "xAxisField",
                                    series: rawData.series,
                                    noLimit: true,
                                }),
                                filters: tileFilters.getValue(),
                            })}

                            {RemoveTileControlFilter({
                                tile,
                                hideBorder: !hasAlternativeBarAxes && !hasAlternativeLineAxes,
                            })}

                            {hasMenuOptions &&
                                MenuOptions({
                                    chartRef,
                                    theme,
                                    tile,
                                    downloadData,
                                    tileFilters,
                                })}

                            {showSwapAxis &&
                                SwapAxis({
                                    tile,
                                    theme,
                                    list: [
                                        hasAlternativeBarAxes && {
                                            label: "Bars",
                                            choices: barKeys,
                                            onSelect: (k) => barKey.onChange(k),
                                            isSelected: (k) => barKey.value === k,
                                            valueToLabel: (key) => tile.style.yAxisBar[key].label || data.barData[key].label,
                                        },
                                        hasAlternativeLineAxes && {
                                            label: "Lines",
                                            choices: lineKeys,
                                            onSelect: (k) => lineKey.onChange(k),
                                            isSelected: (k) => lineKey.value === k,
                                            valueToLabel: (key) => tile.style.yAxisLine[key].label || data.lineData[key].label,
                                        },
                                    ].filter((v) => v),
                                })}
                        </div>
                    )
                );
            },
        ],

        ({rawData, data, controls}, next) =>
            ChartInnerLayout({
                size,
                tile,
                theme,
                next,
                noData: !isDataValid(rawData),
                hasControls: !!controls,
            }),

        [
            "actionMenu",
            ({formatters}, next) =>
                ActionMenu({
                    tile,
                    overrideTile,
                    dimensionFormatter: formatters.dimensionFormatter,
                    disabledTileActions,
                    next,
                }),
        ],

        [
            "onClickPoint",
            ({actionMenu, tileFields, data}, next) => {
                if (!actionMenu.hasActions()) {
                    return next();
                }
                return next(({point, ...other}) => {
                    const dimensionField = tileFields.find((f) => f.$type == "DimensionTileField");
                    const dimensionValue = point.name ?? point.category;

                    const groupField = tileFields.find((f) => f.$type == "CategoryTileField");
                    let groupValue = null;
                    let fieldToValue = {
                        [dimensionField.id]: dimensionValue,
                    };

                    if (groupField) {
                        groupValue = point.series.name;
                        fieldToValue[groupField.id] = groupValue;
                    }

                    const _fieldToValue = tileFields
                        .filter((f) => f.$type == "MeasureTileField")
                        .reduce((obj, field) => {
                            const list = !field.isBarField ? data.lineData : data.barData;
                            let items = null;
                            if (groupField) {
                                const series = list[field.id].series;
                                items = series.find((s) => s.name == groupValue)?.data;
                            } else {
                                const key = Object.keys(list).find((k) => k.indexOf(field.id) >= 0);
                                items = list[key].series.find((s) => s.tileFieldID == field.id)?.data;
                            }

                            obj[field.id] = items?.find((d) => d[0] == dimensionValue)?.[1] ?? null;
                            return obj;
                        }, {});

                    const tileActionData = point.series?.userOptions?.tileActionData;

                    tileActionData?.columns.forEach((c, index) => {
                        fieldToValue[c.tileFieldID] = tileActionData.data[index];
                    });

                    actionMenu?.show?.({
                        point,
                        ...other,
                        fieldToValue: {
                            ...fieldToValue,
                            ..._fieldToValue,
                        },
                    });
                });
            },
        ],

        ({selectedTimezone}, next) =>
            IgnoreUpdate({
                next,
                props: {timezoneId: selectedTimezone?.value},
                when: ({timezoneId}) => timezoneId != selectedTimezone?.value,
            }),

        ({data, rawData2, lineKey, barKey, referenceSeries, isCompare, yAxisBarOptions, yAxisLineOptions, xAxisOptions, isDimensionRealDate, formatters, actionMenu, onClickPoint, controls, selectedTimezone}) => {
            const {multipleAxisBarOption, multipleAxisLineOption} = tile.style;
            const isIndependentBarAxes = multipleAxisBarOption === "IndependentAxes";
            const isIndependentLineAxes = multipleAxisLineOption === "IndependentAxes";

            const defaultYAxisOption = {title: {text: undefined}};

            let options = {
                ...getComboChartOptions({
                    tile,
                    theme,
                    isCompare,
                    isDimensionRealDate,
                    rawData: rawData2,
                    barStack: barKey.value,
                    formatters,
                    onClickPoint,
                    selectedTimezone,
                }),
                xAxis: xAxisOptions,
                // bar y-axes first, line y-axes second. ref. process-combo-chart-data.js
                yAxis: [
                    ...(!isIndependentBarAxes ? [yAxisBarOptions[barKey.value]?.options || defaultYAxisOption] : Object.keys(yAxisBarOptions).map((k) => yAxisBarOptions[k]?.options || defaultYAxisOption)),
                    ...(!isIndependentLineAxes ? [yAxisLineOptions[lineKey.value]?.options || defaultYAxisOption] : Object.keys(yAxisLineOptions).map((k) => yAxisLineOptions[k]?.options || defaultYAxisOption)),
                ],
                // bar series first, line series second. ref. process-combo-chart-data.js
                series: [
                    ...(!isIndependentBarAxes ? data.barData[barKey.value]?.series || [] : flatten1(Object.keys(data.barData).map((k) => data.barData[k].series))),
                    ...(!isIndependentLineAxes ? data.lineData[lineKey.value]?.series || [] : flatten1(Object.keys(data.lineData).map((k) => data.lineData[k].series))),
                    ...(referenceSeries || []),
                ],
            };

            const currentXAxis = Array.isArray(options.xAxis) ? options.xAxis.find((item) => item.visible !== false) : options.xAxis;

            if (actionMenu?.hasActions() && currentXAxis.labels.enabled) {
                currentXAxis.labels.events = {
                    click: function ({x, y}) {
                        onClickPoint?.({
                            x,
                            y,
                            point: {category: this.pos, y: null},
                            onlyDimensionField: true,
                            chart: this.chart,
                        });
                    },
                };
            }

            return (
                <div
                    style={{
                        fontFamily: `"${theme.general.canvas.fontFamily || "Roboto"}", sans-serif`,
                    }}
                    className={cx("combo-chart-25k", !tile?.style?.title.show && "hide-title")}
                    key={getRefreshKey({
                        tile,
                        theme,
                        tileFiltersValue: tileFilters.getValue(),
                        lineKey: lineKey.value,
                        barKey: barKey.value,
                    })}
                >
                    <div className="chart">
                        <RenderChart
                            {...{
                                noScroll,
                                chartRef,
                                options,
                                size,
                                theme,
                                tile,
                                formatters,
                            }}
                        />
                    </div>

                    {controls}
                </div>
            );
        }
    );

const getRefreshKey = ({tile, theme, tileFiltersValue, lineKey, barKey}) =>
    JSON.stringify([keepOnly(tile, ["id", "style", "filters", "groupField", "xAxisField", "yAxisBarFields", "yAxisLineFields", "limit", "sort", "categorySort", "referenceLines"]), theme.dataVisualization.toolTipsAndLegends, tileFiltersValue, lineKey, barKey]);
